<template>
  <div class="help-page" @keydown.esc="clearSearch">
    <!-- ============ Hero + search ============ -->
    <header class="help-hero">
      <div class="hero-row">
        <div class="hero-icon" aria-hidden="true">
          <v-icon size="28" color="#ffb91a">mdi-lifebuoy</v-icon>
        </div>
        <div class="hero-text">
          <h1 class="hero-title">מידע ועזרה</h1>
          <p class="hero-subtitle">כל השלוחות במערכת הטלפונית וכל האפשרויות בממשק, במקום אחד</p>
        </div>
      </div>

      <div class="search-box" :class="{ 'has-value': query }">
        <v-icon class="search-icon" size="22" color="#8a97b8">mdi-magnify</v-icon>
        <input ref="search" v-model="query" type="search" class="search-input" :dir="keyPathQuery ? 'ltr' : 'rtl'"
          placeholder="חפשו לפי נושא או לפי מקשים, למשל: השתקה, שיתוף, 7 8 4"
          aria-label="חיפוש במרכז העזרה" @keydown.enter="openFirstResult">
        <button v-if="query" type="button" class="search-clear" aria-label="ניקוי החיפוש" @click="clearSearch">
          <v-icon size="18" color="#5b6b8c">mdi-close</v-icon>
        </button>
        <span v-else class="search-hint" aria-hidden="true">/</span>
      </div>
    </header>

    <!-- ============ Tabs ============ -->
    <nav class="help-tabs" role="tablist" aria-label="אזורי העזרה">
      <button v-for="t in tabs" :key="t.id" type="button" role="tab" class="help-tab"
        :class="{ active: tab === t.id && !query }" :aria-selected="String(tab === t.id && !query)"
        @click="setTab(t.id)">
        <v-icon size="18" :color="tab === t.id && !query ? '#0e3280' : '#8a97b8'">{{ t.icon }}</v-icon>
        <span>{{ t.label }}</span>
      </button>
      <span v-if="query" class="results-count" aria-live="polite">
        {{ totalResults }} תוצאות עבור <bdi dir="auto">"{{ query }}"</bdi>
      </span>
    </nav>

    <!-- ============ Search results ============ -->
    <section v-if="query" class="results" aria-label="תוצאות חיפוש">
      <div v-if="totalResults === 0" class="empty-state">
        <v-icon size="40" color="#c3cbe0">mdi-text-search</v-icon>
        <p class="empty-title">לא נמצאו תוצאות</p>
        <p class="empty-text">נסו מילה אחרת, למשל "צינתוק" במקום "התראה", או הקישו את מסלול המקשים כמו 1 8 4.</p>
      </div>

      <div v-for="group in resultGroups" v-show="group.items.length" :key="group.id" class="result-group">
        <h2 class="result-group-title">
          <v-icon size="18" color="#0e3280">{{ group.icon }}</v-icon>
          {{ group.title }}
          <span class="result-group-count">{{ group.items.length }}</span>
        </h2>
        <ul class="result-list">
          <li v-for="r in group.items" :key="r.id">
            <button type="button" class="result-item" @click="openResult(r)">
              <span v-if="r.path && r.path.length" class="keycap-path" aria-hidden="true">
                <span v-for="(k, i) in r.path" :key="i" class="keycap keycap-sm">{{ k }}</span>
              </span>
              <span v-else class="result-crumb">{{ r.crumb }}</span>
              <span class="result-body">
                <span class="result-title" v-html="highlight(r.title)"></span>
                <span class="result-snippet" v-html="highlight(r.snippet)"></span>
              </span>
              <span v-if="r.role && r.role !== 'all'" class="role-badge" :class="'role-' + r.role">{{ roleShort(r.role) }}</span>
            </button>
          </li>
        </ul>
      </div>
    </section>

    <!-- ============ Phone tab ============ -->
    <section v-else-if="tab === 'phone'" class="phone-layout" aria-label="המערכת הטלפונית">
      <!-- Tree -->
      <aside class="tree-panel" aria-label="היררכיית השלוחות">
        <div class="tree-head">
          <span class="tree-title">היררכיית השלוחות</span>
          <button type="button" class="tree-toggle-all" @click="toggleAll">
            {{ allExpanded ? 'כיווץ הכל' : 'פתיחת הכל' }}
          </button>
        </div>
        <div class="tree-legend" aria-hidden="true">
          <span><i class="role-dot role-managers"></i> מנהלים</span>
          <span><i class="role-dot role-owner"></i> בעלים בלבד</span>
        </div>
        <ul class="tree" role="tree">
          <li v-for="row in visibleTreeRows" :key="row.id" role="treeitem"
            :aria-expanded="row.hasChildren ? String(isExpanded(row.id)) : null"
            :aria-selected="String(selectedId === row.id)">
            <div class="tree-row" :class="{ selected: selectedId === row.id }"
              :style="{ paddingInlineStart: (10 + row.depth * 18) + 'px' }">
              <button v-if="row.hasChildren" type="button" class="tree-chevron"
                :aria-label="isExpanded(row.id) ? 'כיווץ' : 'הרחבה'" @click="toggle(row.id)">
                <v-icon size="18" color="#8a97b8">{{ isExpanded(row.id) ? 'mdi-chevron-down' : 'mdi-chevron-left' }}</v-icon>
              </button>
              <span v-else class="tree-chevron-spacer"></span>
              <button type="button" class="tree-label" @click="select(row.id)">
                <span v-if="row.key" class="keycap keycap-xs">{{ row.key }}</span>
                <v-icon v-else size="16" color="#0e3280">{{ row.id === 'entry' ? 'mdi-phone-incoming' : 'mdi-dialpad' }}</v-icon>
                <span class="tree-text">{{ row.title }}</span>
                <span v-if="row.role !== 'all'" class="role-dot" :class="'role-' + row.role"
                  :title="roleLabel(row.role)"></span>
              </button>
            </div>
          </li>
        </ul>
      </aside>

      <!-- Detail -->
      <article v-if="selected" class="detail-panel" :key="selected.id">
        <div class="detail-top">
          <button v-if="selectedParent" type="button" class="back-btn" @click="select(selectedParent.id)">
            <v-icon size="18" color="#0e3280">mdi-arrow-right</v-icon>
            {{ selectedParent.title }}
          </button>
          <div v-if="selectedPath.length" class="keycap-path keycap-path-lg" :aria-label="pathLabel(selected)">
            <span class="path-start">
              <v-icon size="16" color="#8a97b8">mdi-phone</v-icon>
            </span>
            <span v-for="(k, i) in selectedPath" :key="i" class="path-step">
              <v-icon size="14" color="#c3cbe0" class="path-sep">mdi-chevron-left</v-icon>
              <span class="keycap" :class="{ 'keycap-hot': i === selectedPath.length - 1 }">{{ k }}</span>
            </span>
          </div>
        </div>

        <div class="detail-head">
          <h2 class="detail-title">{{ selected.title }}</h2>
          <div class="detail-badges">
            <span class="role-badge" :class="'role-' + selected.role">{{ roleLabel(selected.role) }}</span>
            <span v-if="selected.unannounced" class="soft-badge" title="המקש פעיל אבל לא מוזכר בהקראת התפריט">
              <v-icon size="14" color="#5b6b8c">mdi-volume-off</v-icon>
              לא מוקרא בתפריט
            </span>
          </div>
        </div>
        <p class="detail-summary">{{ selected.summary }}</p>

        <div v-if="selected.details && selected.details.length" class="detail-body">
          <p v-for="(d, i) in selected.details" :key="i">{{ d }}</p>
        </div>

        <!-- Modes: שלוחה עם כמה מצבים (שלוחה 3) -->
        <div v-if="modeCards.length" class="modes-block">
          <section v-for="m in modeCards" :key="m.id" class="mode-card"
            :class="{ 'mode-current': m.status && m.status.current }">
            <header class="mode-head">
              <span class="mode-icon" aria-hidden="true">
                <v-icon size="20" color="#ffb91a">{{ m.icon }}</v-icon>
              </span>
              <h3 class="mode-title">{{ m.title }}</h3>
            </header>
            <span v-if="m.status" class="mode-status" :class="'tone-' + m.status.tone">
              <v-icon size="14" class="mode-status-icon">{{ m.status.icon }}</v-icon>
              {{ m.status.label }}
            </span>
            <p class="mode-who"><strong>למי:</strong> {{ m.who }}</p>
            <p v-for="(d, i) in m.details" :key="i" class="mode-text">{{ d }}</p>
            <p v-if="m.skip" class="mode-skip">
              <v-icon size="16" color="#b07d00">mdi-skip-next-circle-outline</v-icon>
              <span>{{ m.skip }}</span>
            </p>
            <button v-if="m.status && m.status.cta" type="button" class="mode-cta" @click="goRoute(m.status.cta.route)">
              {{ m.status.cta.label }}
              <v-icon size="16" color="#0a2357">mdi-arrow-left</v-icon>
            </button>
          </section>
        </div>

        <!-- Children keypad -->
        <div v-if="selected.children && selected.children.length" class="keypad-block">
          <h3 class="block-title">מה אפשר להקיש כאן</h3>
          <div class="keypad" role="group" aria-label="מקשי התפריט">
            <button v-for="cell in keypadCells" :key="cell.key" type="button" class="pad-key"
              :class="{ 'pad-key-ghost': !cell.node }" :disabled="!cell.node"
              :aria-hidden="cell.node ? null : 'true'" :tabindex="cell.node ? null : -1"
              @click="cell.node && pressKey(cell.node)">
              <span class="keycap keycap-pad">{{ cell.key }}</span>
              <span v-if="cell.node" class="pad-label">{{ cell.node.title }}</span>
              <span v-if="cell.node && cell.node.role !== 'all'" class="role-dot" :class="'role-' + cell.node.role"
                :title="roleLabel(cell.node.role)"></span>
            </button>
          </div>
          <div v-if="extraKeys.length" class="keypad-extra">
            <button v-for="c in extraKeys" :key="c.id" type="button" class="pad-key pad-key-wide" @click="pressKey(c)">
              <span class="keycap keycap-pad keycap-multi">{{ c.key }}</span>
              <span class="pad-label">{{ c.title }}</span>
            </button>
          </div>
        </div>

        <!-- Option groups -->
        <div v-for="(g, gi) in (selected.optionGroups || [])" :key="'g' + gi" class="options-block">
          <h3 class="block-title">{{ g.title }}</h3>
          <ul class="option-list">
            <li v-for="(o, oi) in g.items" :key="oi" class="option-row">
              <span class="keycap-pair">
                <span v-for="(d, di) in splitKey(o.key)" :key="di" class="keycap keycap-sm">{{ d }}</span>
              </span>
              <span class="option-text">{{ o.text }}</span>
            </li>
          </ul>
          <p v-if="g.note" class="option-note">{{ g.note }}</p>
        </div>

        <!-- Playback keys -->
        <div v-if="selected.playback" class="options-block playback-block">
          <button type="button" class="playback-toggle" :aria-expanded="String(showPlayback)"
            @click="showPlayback = !showPlayback">
            <v-icon size="18" color="#0e3280">mdi-play-circle-outline</v-icon>
            <span>{{ playbackSets[selected.playback].title }} בזמן ההשמעה</span>
            <v-icon size="18" color="#8a97b8" class="playback-chevron" :class="{ open: showPlayback }">mdi-chevron-down</v-icon>
          </button>
          <div v-show="showPlayback" class="playback-grid">
            <div v-for="pk in playbackSets[selected.playback].keys" :key="pk.key + pk.text" class="playback-item">
              <span class="keycap keycap-sm">{{ pk.key }}</span>
              <span>{{ pk.text }}</span>
            </div>
            <p v-if="playbackSets[selected.playback].note" class="option-note">{{ playbackSets[selected.playback].note }}</p>
          </div>
        </div>

        <!-- Skip tip -->
        <div v-if="selected.skip" class="skip-tip">
          <v-icon size="20" color="#b07d00">mdi-skip-next-circle-outline</v-icon>
          <span>{{ selected.skip }}</span>
        </div>

        <!-- Requires + notices -->
        <div v-if="(selected.requires && selected.requires.length) || (selected.notices && selected.notices.length)"
          class="conditions">
          <div v-for="(r, i) in (selected.requires || [])" :key="'r' + i" class="condition condition-lock">
            <v-icon size="16" color="#5b6b8c">mdi-lock-outline</v-icon>
            <span>{{ r }}</span>
          </div>
          <div v-for="(n, i) in (selected.notices || [])" :key="'n' + i" class="condition">
            <v-icon size="16" color="#5b6b8c">mdi-information-outline</v-icon>
            <span>{{ n }}</span>
          </div>
        </div>

        <!-- Cross link -->
        <button v-if="selected.web" type="button" class="cross-link" @click="goRoute(selected.web.route)">
          <v-icon size="18" color="#0e3280">mdi-monitor</v-icon>
          <span>אפשר גם בממשק: <strong>{{ selected.web.label }}</strong></span>
          <v-icon size="18" color="#0e3280" class="cross-arrow">mdi-arrow-left</v-icon>
        </button>
      </article>
    </section>

    <!-- ============ Web tab ============ -->
    <section v-else-if="tab === 'web'" class="web-layout" aria-label="ממשק הניהול">
      <nav class="web-rail" aria-label="עמודי הממשק">
        <button v-for="p in visibleWebPages" :key="p.id" type="button" class="rail-link"
          :class="{ active: activeWebPage === p.id }" @click="scrollToPage(p.id)">
          <v-icon size="18" :color="activeWebPage === p.id ? '#0e3280' : '#8a97b8'">{{ p.icon }}</v-icon>
          {{ p.title }}
        </button>
      </nav>
      <div class="web-content">
        <section v-for="p in visibleWebPages" :id="p.id" :key="p.id" :ref="p.id" class="web-page">
          <header class="web-page-head">
            <div class="web-page-icon" aria-hidden="true">
              <v-icon size="22" color="#ffb91a">{{ p.icon }}</v-icon>
            </div>
            <div class="web-page-text">
              <h2 class="web-page-title">{{ p.title }}</h2>
              <p class="web-page-summary">{{ p.summary }}</p>
            </div>
            <button type="button" class="open-page-btn" @click="goRoute(p.route)">
              מעבר לעמוד
              <v-icon size="16" color="#0e3280">mdi-arrow-left</v-icon>
            </button>
          </header>
          <dl v-if="p.items.length" class="web-items">
            <div v-for="it in p.items" :id="it.id" :key="it.id" class="web-item"
              :class="{ flash: flashId === it.id }">
              <dt class="web-item-title">{{ it.title }}</dt>
              <dd class="web-item-text">
                {{ it.text }}
                <span v-if="it.phoneNode && nodeMap[it.phoneNode]" class="phone-link-wrap">
                  <button type="button" class="phone-link" @click="openPhoneNode(it.phoneNode)">
                    <v-icon size="14" color="#0e3280">mdi-phone</v-icon>
                    <span>גם בטלפון</span>
                    <span v-for="(k, i) in nodeMap[it.phoneNode].path" :key="i" class="keycap keycap-xs">{{ k }}</span>
                  </button>
                </span>
              </dd>
            </div>
          </dl>
        </section>
      </div>
    </section>

    <!-- ============ Keys & skips tab ============ -->
    <section v-else class="keys-layout" aria-label="מקשי השמעה ודילוגים">
      <div class="keys-intro">
        <h2 class="section-title">מקשים בזמן השמעת הודעות</h2>
        <p class="section-text">הודעות מושמעות מהחדשה לישנה. אותם מקשים עובדים בכל ההשמעות, עם הבדלים קטנים בין האזורים.</p>
      </div>
      <div class="playback-sets">
        <div v-for="(set, sid) in playbackSets" :key="sid" class="playback-set">
          <h3 class="set-title">{{ set.title }}</h3>
          <p class="set-where">{{ set.where }}</p>
          <ul class="set-keys">
            <li v-for="pk in set.keys" :key="pk.key + pk.text">
              <span class="keycap keycap-sm">{{ pk.key }}</span>
              <span>{{ pk.text }}</span>
            </li>
          </ul>
          <p v-if="set.note" class="option-note">{{ set.note }}</p>
        </div>
      </div>

      <div class="keys-intro skip-intro">
        <h2 class="section-title">דילוג על הקלטות מערכת</h2>
        <p class="section-text">לא צריך לחכות לסוף כל הקראה. כך יודעים מתי אפשר לקצר את הדרך.</p>
      </div>
      <ul class="skip-list">
        <li v-for="s in skipRules" :id="s.id" :key="s.id" class="skip-row" :class="{ flash: flashId === s.id, 'skip-none': s.id === 'no-skip' }">
          <div class="skip-keys">
            <template v-if="s.keys.length">
              <span v-for="k in s.keys" :key="k" class="keycap" :class="{ 'keycap-word': k.length > 2 }">{{ k }}</span>
            </template>
            <v-icon v-else size="22" :color="s.id === 'no-skip' ? '#b5bdd3' : '#b07d00'">
              {{ s.id === 'no-skip' ? 'mdi-cancel' : 'mdi-lightning-bolt-outline' }}
            </v-icon>
          </div>
          <div class="skip-body">
            <h3 class="skip-title">{{ s.title }}</h3>
            <p class="skip-text">{{ s.text }}</p>
            <div class="skip-links">
              <button v-if="s.nodeId && nodeMap[s.nodeId]" type="button" class="phone-link" @click="openPhoneNode(s.nodeId)">
                <v-icon size="14" color="#0e3280">mdi-phone</v-icon>
                <span>{{ nodeMap[s.nodeId].title }}</span>
              </button>
              <button v-if="s.web" type="button" class="phone-link" @click="goRoute(s.web.route)">
                <v-icon size="14" color="#0e3280">mdi-monitor</v-icon>
                <span>{{ s.web.label }}</span>
              </button>
            </div>
          </div>
        </li>
      </ul>
    </section>
  </div>
</template>

<script>
import ApiServices from '@/services/api.service';
import {
  phoneTree, entryNode, webPages, PLAYBACK_SETS, SKIP_RULES, SYNONYMS, ROLES,
} from './helpContent.js';

// ---------- Search helpers ----------
// נרמול באורך קבוע (תו מול תו) כדי שאינדקסים יתאימו לטקסט המקורי בהדגשה
const FINALS = { 'ך': 'כ', 'ם': 'מ', 'ן': 'נ', 'ף': 'פ', 'ץ': 'צ' };
function normKeepLength(s) {
  return String(s || '').toLowerCase().replace(/[ךםןףץ]/g, (c) => FINALS[c]);
}
function norm(s) {
  return normKeepLength(s)
    .replace(/[\u0591-\u05C7]/g, '')          // ניקוד וטעמים
    .replace(/["'״׳`]/g, '')                  // גרשיים
    .replace(/[^\u05D0-\u05EAa-z0-9#*\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
const PREFIXES = ['ו', 'ה', 'ב', 'ל', 'מ', 'ש', 'כ'];
const SYN_GROUPS = SYNONYMS.map((g) => g.map(norm));

function variantsFor(token) {
  const out = new Set([token]);
  if (token.length > 3 && PREFIXES.includes(token[0])) out.add(token.slice(1));
  for (const v of Array.from(out)) {
    for (const group of SYN_GROUPS) {
      if (group.some((w) => w === v || (v.length >= 3 && (w.startsWith(v) || v.startsWith(w))))) {
        group.forEach((w) => out.add(w));
      }
    }
  }
  return Array.from(out).filter((v) => v.length >= 2 || /[0-9#*]/.test(v));
}

const KEYPAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

// הסטטוס של כל מצב בשלוחה 3, לפי ai_access מהשרת (server/src/util/aiAccess.js)
const PLAYING_NOW = { label: 'מושמע אצלכם כרגע', tone: 'current', icon: 'mdi-volume-high', current: true };
const MODE_STATUS = {
  active: {
    ai: { label: 'פעיל אצלכם', tone: 'on', icon: 'mdi-check-circle', current: true },
  },
  ravkol: {
    ai: { label: 'פעיל למתקשרים עם מנוי רב קול', tone: 'on', icon: 'mdi-check-circle', current: true },
    instructions: { label: 'למתקשרים בלי מנוי', tone: 'neutral', icon: 'mdi-account-outline' },
  },
  upgrade: {
    ai: { label: 'זמין בחבילה בתשלום', tone: 'upsell', icon: 'mdi-star-four-points-outline', cta: { label: 'לשדרוג החבילה', route: '/settings/page4' } },
    instructions: PLAYING_NOW,
  },
  off: {
    ai: { label: 'כבוי בהגדרות שלכם', tone: 'neutral', icon: 'mdi-power', cta: { label: 'להפעלה בהגדרות', route: '/settings/page1' } },
    instructions: PLAYING_NOW,
  },
  blocked: {
    ai: { label: 'לא זמין במערכת שלכם', tone: 'neutral', icon: 'mdi-lock-outline' },
    instructions: PLAYING_NOW,
  },
  unavailable: {
    ai: { label: 'לא זמין כרגע', tone: 'neutral', icon: 'mdi-clock-outline' },
    instructions: PLAYING_NOW,
  },
};

export default {
  name: 'HelpComp',
  data() {
    return {
      tab: 'phone',
      query: '',
      selectedId: 'main',
      expanded: { main: true },
      showPlayback: true,
      activeWebPage: webPages[0].id,
      flashId: null,
      tabs: [
        { id: 'phone', label: 'המערכת הטלפונית', icon: 'mdi-dialpad' },
        { id: 'web', label: 'ממשק הניהול', icon: 'mdi-monitor-dashboard' },
        { id: 'keys', label: 'מקשי השמעה ודילוגים', icon: 'mdi-skip-next-circle-outline' },
      ],
      playbackSets: PLAYBACK_SETS,
      skipRules: SKIP_RULES,
      keypadKeys: KEYPAD,
      profileFlags: [], // variants פעילים למערכת הזו: 'telebip', 'surveysOff'
      aiAccess: null,   // מצב העוזר הקולי במערכת (ai_access מהשרת). null = לא ידוע
    };
  },

  computed: {
    role() {
      return (this.$store && this.$store.state.role) || 'user';
    },

    // כל השלוחות, עם מסלול מקשים והורה
    activeVariants() {
      return new Set(this.profileFlags);
    },

    nodeMap() {
      const map = {};
      const walk = (node, parent, path) => {
        const p = node.key ? path.concat(node.key) : path;
        const resolved = this.resolveNode(node);
        map[node.id] = { ...resolved, parentId: parent ? parent.id : null, path: p };
        // הילדים של הצומת אחרי ה-variant: variant עם children: [] מסתיר את תתי-השלוחות גם בחיפוש ובעץ
        (resolved.children || []).forEach((c) => walk(c, node, p));
      };
      walk(entryNode, null, []);
      walk(phoneTree, null, []);
      return map;
    },

    roots() {
      return [entryNode, phoneTree];
    },

    visibleTreeRows() {
      const rows = [];
      const walk = (node, depth) => {
        const hasChildren = !!(node.children && node.children.length);
        const r = this.nodeMap[node.id];
        rows.push({ id: node.id, key: node.key, title: r.title, role: r.role, depth, hasChildren });
        if (hasChildren && this.expanded[node.id]) node.children.forEach((c) => walk(c, depth + 1));
      };
      this.roots.forEach((r) => walk(r, 0));
      return rows;
    },

    allExpanded() {
      return Object.values(this.nodeMap)
        .filter((n) => n.children && n.children.length)
        .every((n) => this.expanded[n.id]);
    },

    selected() {
      return this.nodeMap[this.selectedId] || null;
    },
    selectedParent() {
      return this.selected && this.selected.parentId ? this.nodeMap[this.selected.parentId] : null;
    },
    selectedPath() {
      return this.selected ? this.selected.path : [];
    },
    // שלוחה עם כמה מצבים (שלוחה 3): לכל מצב - למי מיועד, ומה המצב במערכת של הקורא
    modeCards() {
      if (!this.selected || !this.selected.modes) return [];
      const byAccess = MODE_STATUS[this.aiAccess] || {};
      return this.selected.modes.map((m) => ({ ...m, status: byAccess[m.id] || null }));
    },

    keypadCells() {
      const children = (this.selected && this.selected.children) || [];
      return KEYPAD.map((k) => {
        const c = children.find((ch) => ch.key === k);
        return { key: k, node: c ? this.nodeMap[c.id] : null };
      });
    },
    extraKeys() {
      if (!this.selected || !this.selected.children) return [];
      return this.selected.children.filter((c) => c.key && c.key.length > 1).map((c) => this.nodeMap[c.id]);
    },

    visibleWebPages() {
      if (this.role === 'admin') return webPages;
      return webPages.filter((p) => p.roles.includes(this.role));
    },

    // ---------- מסמכי החיפוש ----------
    searchDocs() {
      const docs = [];
      Object.values(this.nodeMap).forEach((n) => {
        const opts = (n.optionGroups || []).map((g) => g.title + ' ' + g.items.map((i) => i.text).join(' ')).join(' ');
        docs.push({
          group: 'phone', id: n.id, nodeId: n.id, role: n.role,
          title: n.title, snippet: n.summary, path: n.path,
          fields: {
            title: norm(n.title),
            keywords: norm((n.keywords || []).join(' ')),
            summary: norm(n.summary),
            body: norm([(n.details || []).join(' '), opts, (n.requires || []).join(' '), (n.notices || []).join(' '), n.skip || '',
              (n.modes || []).map((m) => `${m.title} ${m.who} ${m.details.join(' ')}`).join(' ')].join(' ')),
          },
          keyPath: n.path.join(''),
        });
      });
      this.visibleWebPages.forEach((p) => {
        docs.push({
          group: 'web', id: p.id, pageId: p.id, route: p.route, title: p.title, snippet: p.summary, crumb: 'עמוד',
          fields: { title: norm(p.title), keywords: '', summary: norm(p.summary), body: '' },
        });
        p.items.forEach((it) => {
          docs.push({
            group: 'web', id: it.id, pageId: p.id, route: it.route || p.route, title: it.title, snippet: it.text, crumb: p.title,
            fields: { title: norm(it.title), keywords: norm((it.keywords || []).join(' ')), summary: norm(it.text), body: norm(p.title) },
          });
        });
      });
      SKIP_RULES.forEach((s) => {
        docs.push({
          group: 'tips', id: s.id, skipId: s.id, title: s.title, snippet: s.text, crumb: 'דילוג',
          fields: { title: norm(s.title), keywords: norm('דילוג לדלג ' + s.keys.join(' ')), summary: norm(s.text), body: '' },
        });
      });
      return docs;
    },

    queryTokens() {
      return norm(this.query).split(' ').filter(Boolean);
    },

    // חיפוש לפי מסלול מקשים: "7 8 4", "784", "7-8-4"
    keyPathQuery() {
      const raw = this.query.trim();
      if (!raw || !/^[0-9#*\s,.\-›>←]+$/.test(raw)) return null;
      return raw.replace(/[^0-9#*]/g, '');
    },

    searchResults() {
      if (!this.query.trim()) return [];
      if (this.keyPathQuery) {
        const q = this.keyPathQuery;
        return this.searchDocs
          .filter((d) => d.group === 'phone' && d.keyPath && d.keyPath.startsWith(q))
          .map((d) => ({ ...d, score: d.keyPath === q ? 100 : 50 - d.keyPath.length }))
          .sort((a, b) => b.score - a.score)
          .slice(0, 30);
      }
      const tokens = this.queryTokens.map(variantsFor);
      if (!tokens.length) return [];
      const W = { title: 6, keywords: 4, summary: 2, body: 1 };
      const scoreDoc = (doc, requireAll) => {
        let total = 0;
        let matched = 0;
        tokens.forEach((variants) => {
          let best = 0;
          Object.keys(W).forEach((f) => {
            const text = doc.fields[f];
            if (text && variants.some((v) => text.includes(v))) best = Math.max(best, W[f]);
          });
          if (best) { matched += 1; total += best; }
        });
        if (requireAll && matched < tokens.length) return 0;
        return total;
      };
      let res = this.searchDocs.map((d) => ({ ...d, score: scoreDoc(d, true) })).filter((d) => d.score > 0);
      if (!res.length) {
        res = this.searchDocs.map((d) => ({ ...d, score: scoreDoc(d, false) * 0.5 })).filter((d) => d.score > 0);
      }
      return res.sort((a, b) => b.score - a.score).slice(0, 40);
    },

    resultGroups() {
      const r = this.searchResults;
      return [
        { id: 'phone', title: 'במערכת הטלפונית', icon: 'mdi-dialpad', items: r.filter((x) => x.group === 'phone') },
        { id: 'web', title: 'בממשק הניהול', icon: 'mdi-monitor-dashboard', items: r.filter((x) => x.group === 'web') },
        { id: 'tips', title: 'דילוגים וקיצורים', icon: 'mdi-skip-next-circle-outline', items: r.filter((x) => x.group === 'tips') },
      ];
    },
    totalResults() {
      return this.searchResults.length;
    },

    highlightTerms() {
      if (this.keyPathQuery) return [];
      const set = new Set();
      this.queryTokens.forEach((t) => variantsFor(t).forEach((v) => { if (v.length >= 2) set.add(v); }));
      return Array.from(set).sort((a, b) => b.length - a.length);
    },
  },

  watch: {
    query(v) {
      this.syncUrl({ q: v || undefined });
    },
  },

  created() {
    this.loadSystemProfile();
    const { node, q, tab } = this.$route.query || {};
    if (tab && this.tabs.some((t) => t.id === tab)) this.tab = tab;
    if (node && this.nodeMap[node]) {
      this.tab = 'phone';
      this.revealNode(node);
    }
    if (q) this.query = String(q);
  },

  mounted() {
    window.addEventListener('keydown', this.onGlobalKey);
    this.setupSpy();
  },
  updated() {
    this.setupSpy();
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onGlobalKey);
    if (this.spy) this.spy.disconnect();
  },

  methods: {
    // התאמת העזרה למערכת: TELEBIP (מקש 8 אחר) ומצב העוזר הקולי (שלוחה 3).
    // אם הבקשה נכשלת - מוצג התוכן הרגיל.
    async loadSystemProfile() {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.VUE_APP_BASE_URL}/settings/get_settings_details`, ApiServices.requestOptions('GET', '', token));
        if (res.status !== 200) return;
        const d = await res.json();
        const flags = [];
        if (d.agent_profile === 'telebip') flags.push('telebip');
        // שלוחת הסקרים לא מאופשרת במערכת (ע"י האדמין או הסוכן)
        if (d.exten_permissions && !d.exten_permissions.surveys) flags.push('surveysOff');
        this.profileFlags = flags;
        this.aiAccess = d.ai_access || null;
      } catch (e) {
        /* תוכן רגיל */
      }
    },

    resolveNode(node) {
      if (!node.variants) return node;
      let n = node;
      node.variants.forEach((v) => { if (this.activeVariants.has(v.when)) n = { ...n, ...v }; });
      return n;
    },

    roleLabel(r) { return (ROLES[r] || ROLES.all).label; },
    roleShort(r) { return (ROLES[r] || ROLES.all).short; },

    splitKey(k) {
      // קוד מרובה ספרות (10, 32, *1) מוצג כמקש אחד, כמו שהמשתמש חושב עליו
      return [String(k)];
    },

    pathLabel(node) {
      return node.path.length ? 'מסלול מקשים: ' + node.path.join(' ואז ') : node.title;
    },


    isExpanded(id) { return !!this.expanded[id]; },
    toggle(id) { this.$set(this.expanded, id, !this.expanded[id]); },
    toggleAll() {
      const open = !this.allExpanded;
      const next = {};
      if (open) Object.values(this.nodeMap).forEach((n) => { if (n.children && n.children.length) next[n.id] = true; });
      else next.main = true;
      this.expanded = next;
    },

    revealNode(id) {
      let cur = this.nodeMap[id];
      const next = { ...this.expanded };
      while (cur) {
        if (cur.children && cur.children.length) next[cur.id] = true;
        cur = cur.parentId ? this.nodeMap[cur.parentId] : null;
      }
      this.expanded = next;
      this.selectedId = id;
    },

    select(id) {
      this.revealNode(id);
      this.syncUrl({ node: id, tab: undefined });
      this.$nextTick(() => {
        const panel = this.$el.querySelector('.detail-panel');
        if (panel && window.innerWidth < 960) panel.scrollIntoView({ behavior: this.motionOk() ? 'smooth' : 'auto', block: 'start' });
      });
    },

    pressKey(node) {
      this.select(node.id);
    },

    setTab(id) {
      this.query = '';
      this.tab = id;
      this.syncUrl({ tab: id === 'phone' ? undefined : id, node: undefined });
    },

    openPhoneNode(id) {
      this.query = '';
      this.tab = 'phone';
      this.select(id);
      this.$nextTick(() => this.$el.scrollIntoView({ block: 'start' }));
    },

    scrollToPage(id) {
      this.activeWebPage = id;
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: this.motionOk() ? 'smooth' : 'auto', block: 'start' });
    },

    // מסמן בסרגל את העמוד שנמצא כרגע בראש המסך
    setupSpy() {
      if (this.tab !== 'web' || this.query || !('IntersectionObserver' in window)) {
        if (this.spy) { this.spy.disconnect(); this.spy = null; }
        return;
      }
      if (this.spy) return;
      this.spy = new IntersectionObserver((entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) this.activeWebPage = visible[0].target.id;
      }, { rootMargin: '-10% 0px -70% 0px' });
      this.$el.querySelectorAll('.web-page').forEach((el) => this.spy.observe(el));
    },

    flash(id) {
      this.flashId = id;
      setTimeout(() => { if (this.flashId === id) this.flashId = null; }, 1600);
    },

    openResult(r) {
      if (r.group === 'phone') {
        this.openPhoneNode(r.nodeId);
      } else if (r.group === 'web') {
        this.query = '';
        this.tab = 'web';
        this.$nextTick(() => {
          this.scrollToPage(r.pageId);
          if (r.id !== r.pageId) {
            const el = document.getElementById(r.id);
            if (el) el.scrollIntoView({ behavior: this.motionOk() ? 'smooth' : 'auto', block: 'center' });
            this.flash(r.id);
          }
        });
      } else {
        this.query = '';
        this.tab = 'keys';
        this.$nextTick(() => {
          const el = document.getElementById(r.skipId);
          if (el) el.scrollIntoView({ behavior: this.motionOk() ? 'smooth' : 'auto', block: 'center' });
          this.flash(r.skipId);
        });
      }
    },

    openFirstResult() {
      if (this.searchResults.length) this.openResult(this.searchResults[0]);
    },

    clearSearch() {
      this.query = '';
    },

    goRoute(route) {
      if (this.$route.path !== route) this.$router.push({ path: route }).catch(() => {});
    },

    syncUrl(patch) {
      const query = { ...this.$route.query, ...patch };
      Object.keys(query).forEach((k) => { if (query[k] === undefined || query[k] === '') delete query[k]; });
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ query }).catch(() => {});
      }
    },

    onGlobalKey(e) {
      const tag = (e.target && e.target.tagName) || '';
      if (e.key === '/' && !/INPUT|TEXTAREA|SELECT/.test(tag)) {
        e.preventDefault();
        if (this.$refs.search) this.$refs.search.focus();
      }
    },

    motionOk() {
      return !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    },

    // הדגשת מילות החיפוש, עם escape מלא
    highlight(text) {
      const src = String(text || '');
      const esc = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      const terms = this.highlightTerms;
      if (!terms.length) return esc(src);
      const hay = normKeepLength(src);
      const marks = new Array(src.length).fill(false);
      terms.forEach((t) => {
        let i = hay.indexOf(t);
        while (i !== -1) {
          for (let j = i; j < i + t.length; j += 1) marks[j] = true;
          i = hay.indexOf(t, i + t.length);
        }
      });
      let out = '';
      let open = false;
      for (let i = 0; i < src.length; i += 1) {
        if (marks[i] && !open) { out += '<mark>'; open = true; }
        if (!marks[i] && open) { out += '</mark>'; open = false; }
        out += esc(src[i]);
      }
      if (open) out += '</mark>';
      return out;
    },
  },
};
</script>

<style scoped>
/* ================= Tokens ================= */
.help-page {
  --navy: #0e3280;
  --navy-deep: #0a2357;
  --gold: #ffb91a;
  --gold-soft: #fff4d6;
  --ink: #183677;
  --text: #34405c;
  --muted: #5b6b8c;
  --faint: #8a97b8;
  --line: rgba(13, 44, 109, 0.09);
  --surface: #ffffff;
  --page: #f7f8fb;
  --tint: #eef2fa;

  font-family: 'Heebo', sans-serif;
  color: var(--text);
  padding: 20px 28px 48px;
  min-height: 100%;
  background:
    radial-gradient(circle at 100% 0%, rgba(255, 185, 26, 0.05), transparent 38%),
    var(--page);
}

/* ================= Hero ================= */
.help-hero {
  position: relative;
  border-radius: 16px;
  padding: 20px 24px 22px;
  background: linear-gradient(135deg, var(--navy) 0%, var(--navy-deep) 100%);
  box-shadow: 0 8px 20px rgba(10, 35, 87, 0.22);
  overflow: hidden;
}
.help-hero::after {
  content: '';
  position: absolute;
  width: 220px;
  height: 220px;
  top: -110px;
  inset-inline-end: -60px;
  border-radius: 50%;
  background: rgba(255, 185, 26, 0.16);
  filter: blur(46px);
  pointer-events: none;
}
.hero-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}
.hero-icon {
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 185, 26, 0.35);
}
.hero-title {
  color: #fff;
  font-size: 22px;
  font-weight: 800;
  margin: 0;
  line-height: 1.25;
}
.hero-subtitle {
  color: rgba(255, 255, 255, 0.74);
  font-size: 14px;
  margin: 2px 0 0;
}

/* Search */
.search-box {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border-radius: 12px;
  padding: 0 14px;
  height: 52px;
  max-width: 760px;
  box-shadow: 0 4px 14px rgba(5, 20, 55, 0.25);
  border: 2px solid transparent;
  transition: border-color 0.2s ease;
}
.search-box:focus-within {
  border-color: var(--gold);
}
.search-input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  font: inherit;
  font-size: 16px;
  color: var(--ink);
  height: 100%;
  text-align: right;
}
.search-input:focus,
.search-input:focus-visible {
  outline: none;
}
.search-input::placeholder {
  color: #9aa6c2;
}
.search-input::-webkit-search-cancel-button {
  display: none;
}
.search-clear {
  display: flex;
  border-radius: 8px;
  padding: 4px;
}
.search-clear:hover {
  background: var(--tint);
}
.search-hint {
  font-family: inherit;
  font-size: 12px;
  color: var(--faint);
  border: 1px solid #d9dfee;
  border-bottom-width: 2px;
  border-radius: 6px;
  padding: 1px 7px;
}

/* ================= Tabs ================= */
.help-tabs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin: 18px 0 16px;
  border-bottom: 1px solid var(--line);
}
.help-tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 10px 14px 12px;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  color: var(--muted);
  border-bottom: 3px solid transparent;
  margin-bottom: -1px;
  transition: color 0.2s ease, border-color 0.2s ease;
}
.help-tab:hover {
  color: var(--ink);
}
.help-tab.active {
  color: var(--navy);
  border-bottom-color: var(--gold);
}
.results-count {
  margin-inline-start: auto;
  font-size: 13px;
  color: var(--muted);
  padding-bottom: 8px;
}

/* ================= Keycaps (the signature) ================= */
.keycap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 8px;
  border-radius: 9px;
  font-family: 'Heebo', sans-serif;
  font-size: 17px;
  font-weight: 800;
  color: var(--navy);
  background: linear-gradient(180deg, #ffffff 0%, #eef2fa 100%);
  border: 1px solid rgba(14, 50, 128, 0.16);
  box-shadow: 0 2px 0 rgba(14, 50, 128, 0.2), inset 0 1px 0 #fff;
  line-height: 1;
  direction: ltr;
}
.keycap-hot {
  background: linear-gradient(180deg, #ffc93f 0%, var(--gold) 100%);
  border-color: #e6a200;
  box-shadow: 0 2px 0 #c98d00, inset 0 1px 0 rgba(255, 255, 255, 0.5);
  color: var(--navy-deep);
}
.keycap-sm {
  min-width: 28px;
  height: 28px;
  font-size: 14px;
  border-radius: 7px;
  padding: 0 6px;
}
.keycap-xs {
  min-width: 22px;
  height: 22px;
  font-size: 12px;
  border-radius: 6px;
  padding: 0 5px;
  box-shadow: 0 1px 0 rgba(14, 50, 128, 0.2);
}
.keycap-pad {
  min-width: 44px;
  height: 44px;
  font-size: 20px;
  border-radius: 11px;
}
.keycap-multi {
  min-width: 58px;
  letter-spacing: 1px;
}
.keycap-word {
  font-size: 12px;
  font-weight: 700;
  padding: 0 10px;
  direction: rtl;
}
.keycap-path {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  direction: ltr;
  flex-shrink: 0;
}
.keycap-path-lg {
  gap: 6px;
}
.keycap-pair {
  display: inline-flex;
  gap: 3px;
  direction: ltr;
  flex-shrink: 0;
}
.path-start {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--tint);
}
.path-step {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.path-sep {
  transform: scaleX(-1);
}

/* ================= Phone layout ================= */
.phone-layout {
  display: grid;
  grid-template-columns: 330px minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

/* Tree */
.tree-panel {
  position: sticky;
  top: 12px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 16px;
  box-shadow: 0 6px 18px rgba(13, 44, 109, 0.06);
  max-height: calc(100vh - 110px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.tree-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom: 1px solid var(--line);
}
.tree-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
}
.tree-toggle-all {
  font: inherit;
  font-size: 12px;
  color: var(--navy);
  padding: 3px 8px;
  border-radius: 6px;
}
.tree-toggle-all:hover {
  background: var(--tint);
}
.tree-legend {
  display: flex;
  gap: 14px;
  padding: 8px 16px;
  font-size: 12px;
  color: var(--muted);
  border-bottom: 1px solid var(--line);
}
.tree-legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.tree-legend .role-dot {
  margin: 0;
}
.tree {
  list-style: none;
  padding: 6px 0 10px;
  margin: 0;
  overflow-y: auto;
}
.tree-row {
  display: flex;
  align-items: center;
  gap: 2px;
  padding-inline-end: 8px;
  border-inline-start: 3px solid transparent;
}
.tree-row.selected {
  background: var(--tint);
  border-inline-start-color: var(--gold);
}
.tree-chevron,
.tree-chevron-spacer {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}
.tree-chevron:hover {
  background: rgba(14, 50, 128, 0.06);
}
.tree-label {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  font: inherit;
  font-size: 14px;
  color: var(--text);
  text-align: start;
  border-radius: 6px;
}
.tree-row.selected .tree-label {
  color: var(--navy);
  font-weight: 700;
}
.tree-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Role colors */
.role-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-inline-start: auto;
}
.role-dot.role-managers { background: var(--gold); }
.role-dot.role-owner { background: var(--navy); }
.role-badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
}
.role-badge.role-all { background: var(--tint); color: var(--muted); }
.role-badge.role-managers { background: var(--gold-soft); color: #8a6100; }
.role-badge.role-owner { background: var(--navy); color: #fff; }
.soft-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--muted);
  padding: 3px 10px;
  border-radius: 20px;
  border: 1px dashed #c3cbe0;
}

/* Detail */
.detail-panel {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 20px;
  box-shadow: 0 6px 18px rgba(13, 44, 109, 0.07);
  padding: 22px 28px 26px;
  min-height: 420px;
  animation: panelIn 0.28s cubic-bezier(0.22, 1, 0.36, 1);
}
@keyframes panelIn {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
}
.detail-top:empty {
  display: none;
}
.detail-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font: inherit;
  font-size: 13px;
  color: var(--navy);
  padding: 5px 10px 5px 12px;
  border-radius: 8px;
  background: var(--tint);
}
.back-btn:hover {
  background: #e3e9f7;
}
.detail-head {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.detail-title {
  font-size: 26px;
  font-weight: 800;
  color: var(--ink);
  margin: 0;
  line-height: 1.2;
}
.detail-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.detail-summary {
  font-size: 16px;
  color: var(--muted);
  margin: 6px 0 0;
}
.detail-body {
  margin-top: 16px;
  max-width: 68ch;
}
.detail-body p {
  font-size: 15px;
  line-height: 1.75;
  margin: 0 0 8px;
}

.block-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--faint);
  margin: 0 0 10px;
}

/* Modes (שלוחה 3) */
.modes-block {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 22px;
  max-width: 820px;
}
.mode-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px 18px 18px;
  border-radius: 16px;
  background: var(--page);
  border: 1px solid var(--line);
  border-inline-start: 4px solid transparent;
}
.mode-card.mode-current {
  background: #fff;
  border-inline-start-color: var(--gold);
  box-shadow: 0 6px 18px rgba(13, 44, 109, 0.08);
}
.mode-head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.mode-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--navy) 0%, var(--navy-deep) 100%);
}
.mode-title {
  font-size: 18px;
  font-weight: 800;
  color: var(--ink);
  margin: 0;
}
.mode-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
  font-size: 12.5px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
}
.tone-on { background: var(--tint); color: var(--navy); }
.tone-on .mode-status-icon { color: var(--navy) !important; }
.tone-current { background: var(--navy); color: #fff; }
.tone-current .mode-status-icon { color: var(--gold) !important; }
.tone-upsell { background: var(--gold-soft); color: #7a5600; box-shadow: inset 0 0 0 1px rgba(255, 185, 26, 0.6); }
.tone-upsell .mode-status-icon { color: #b07d00 !important; }
.tone-neutral { background: #f0f2f7; color: var(--muted); }
.tone-neutral .mode-status-icon { color: var(--muted) !important; }
.mode-who {
  font-size: 14px;
  color: var(--ink);
  margin: 12px 0 6px;
  line-height: 1.5;
}
.mode-text {
  font-size: 14px;
  line-height: 1.65;
  margin: 0 0 6px;
}
.mode-skip {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 13px;
  color: #6b4b00;
  margin: 4px 0 0;
}
.mode-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: auto;
  transform: translateY(8px);
  padding: 9px 16px;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  color: var(--navy-deep);
  border-radius: 10px;
  background: linear-gradient(180deg, #ffc93f 0%, var(--gold) 100%);
  box-shadow: 0 2px 0 #c98d00;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.mode-cta:hover {
  box-shadow: 0 3px 0 #c98d00, 0 6px 14px rgba(255, 185, 26, 0.35);
}
.mode-cta:active {
  transform: translateY(10px);
  box-shadow: 0 0 0 #c98d00;
}

/* Keypad */
.keypad-block {
  margin-top: 24px;
}
.keypad {
  direction: ltr;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  max-width: 640px;
}
.pad-key {
  direction: rtl;
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 64px;
  padding: 10px 12px;
  border-radius: 14px;
  background: var(--page);
  border: 1px solid var(--line);
  font: inherit;
  text-align: start;
  transition: background 0.18s ease, border-color 0.18s ease;
}
button.pad-key:not(:disabled):hover {
  background: #fff;
  border-color: rgba(14, 50, 128, 0.22);
}
button.pad-key:not(:disabled):hover .keycap {
  background: linear-gradient(180deg, #ffc93f 0%, var(--gold) 100%);
  border-color: #e6a200;
  box-shadow: 0 2px 0 #c98d00;
  color: var(--navy-deep);
}
button.pad-key:not(:disabled):active .keycap {
  transform: translateY(2px);
  box-shadow: 0 0 0 #c98d00;
}
.pad-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  line-height: 1.35;
}
.pad-key .role-dot {
  position: absolute;
  top: 9px;
  inset-inline-end: 9px;
}
.pad-key-ghost {
  cursor: default;
  background: transparent;
  border-style: dashed;
  border-color: rgba(14, 50, 128, 0.1);
  justify-content: flex-start;
}
.pad-key-ghost .keycap {
  opacity: 0.32;
  box-shadow: none;
}
.keypad-extra {
  direction: ltr;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  max-width: 640px;
  margin-top: 10px;
}

/* Options */
.options-block {
  margin-top: 22px;
}
.option-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 6px 18px;
}
.option-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  font-size: 14px;
  line-height: 1.45;
}
.option-note {
  font-size: 13px;
  color: var(--muted);
  margin: 8px 0 0;
}

/* Playback */
.playback-block {
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 4px 14px;
  background: var(--page);
}
.playback-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 0;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
}
.playback-chevron {
  margin-inline-start: auto;
  transition: transform 0.2s ease;
}
.playback-chevron.open {
  transform: rotate(180deg);
}
.playback-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 6px 16px;
  padding: 2px 0 12px;
}
.playback-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13.5px;
}

/* Skip tip + conditions */
.skip-tip {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 20px;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--gold-soft);
  color: #6b4b00;
  font-size: 14px;
  line-height: 1.55;
}
.conditions {
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.condition {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13.5px;
  color: var(--muted);
  line-height: 1.5;
}
.cross-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 22px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid rgba(14, 50, 128, 0.18);
  font: inherit;
  font-size: 14px;
  color: var(--navy);
  background: #fff;
  transition: background 0.18s ease;
}
.cross-link:hover {
  background: var(--tint);
}

/* ================= Search results ================= */
.results {
  max-width: 960px;
}
.result-group {
  margin-bottom: 22px;
}
.result-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: var(--ink);
  margin: 0 0 8px;
}
.result-group-count {
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
  background: var(--tint);
  border-radius: 10px;
  padding: 1px 8px;
}
.result-list {
  list-style: none;
  padding: 0;
  margin: 0;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 14px;
  overflow: hidden;
}
.result-list li + li {
  border-top: 1px solid var(--line);
}
.result-item {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 12px 16px;
  font: inherit;
  text-align: start;
  transition: background 0.15s ease;
}
.result-item:hover {
  background: #fafbfe;
}
.result-crumb {
  flex-shrink: 0;
  min-width: 96px;
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
}
.result-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.result-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--ink);
}
.result-snippet {
  font-size: 13.5px;
  color: var(--muted);
  line-height: 1.5;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.results >>> mark {
  background: var(--gold-soft);
  color: inherit;
  border-radius: 3px;
  padding: 0 1px;
  box-shadow: inset 0 -2px 0 rgba(255, 185, 26, 0.7);
}
.empty-state {
  text-align: center;
  padding: 48px 20px;
}
.empty-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--ink);
  margin: 10px 0 4px;
}
.empty-text {
  font-size: 14px;
  color: var(--muted);
  margin: 0 auto;
  max-width: 46ch;
}

/* ================= Web layout ================= */
.web-layout {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}
.web-rail {
  position: sticky;
  top: 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.rail-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 14px;
  color: var(--muted);
  text-align: start;
  border-radius: 8px;
  border-inline-start: 3px solid transparent;
}
.rail-link:hover {
  color: var(--ink);
  background: rgba(14, 50, 128, 0.04);
}
.rail-link.active {
  color: var(--navy);
  font-weight: 700;
  border-inline-start-color: var(--gold);
  background: var(--surface);
}
.web-content {
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-width: 900px;
}
.web-page {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 18px 22px 8px;
  scroll-margin-top: 12px;
}
.web-page-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 12px;
}
.web-page-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--navy) 0%, var(--navy-deep) 100%);
}
.web-page-text {
  flex: 1;
  min-width: 0;
}
.web-page-title {
  font-size: 19px;
  font-weight: 800;
  color: var(--ink);
  margin: 0;
}
.web-page-summary {
  font-size: 14px;
  color: var(--muted);
  margin: 1px 0 0;
}
.open-page-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: var(--navy);
  padding: 7px 12px;
  border-radius: 10px;
  border: 1px solid rgba(14, 50, 128, 0.18);
  white-space: nowrap;
}
.open-page-btn:hover {
  background: var(--tint);
}
.web-items {
  margin: 0;
  border-top: 1px solid var(--line);
}
.web-item {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr);
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid var(--line);
  scroll-margin-top: 80px;
  transition: background 0.3s ease;
}
.web-item:last-child {
  border-bottom: 0;
}
.web-item-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
}
.web-item-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.65;
  max-width: 66ch;
}
.phone-link-wrap {
  display: block;
  margin-top: 6px;
}
.phone-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: var(--navy);
  padding: 3px 8px 3px 6px;
  border-radius: 8px;
  background: var(--tint);
}
.phone-link:hover {
  background: #e1e8f7;
}
.flash {
  animation: flashRow 1.6s ease;
}
@keyframes flashRow {
  0%, 30% { background: var(--gold-soft); }
  100% { background: transparent; }
}

/* ================= Keys tab ================= */
.keys-intro {
  max-width: 70ch;
  margin-bottom: 14px;
}
.skip-intro {
  margin-top: 34px;
}
.section-title {
  font-size: 19px;
  font-weight: 800;
  color: var(--ink);
  margin: 0 0 4px;
}
.section-text {
  font-size: 14px;
  color: var(--muted);
  margin: 0;
}
.playback-sets {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}
.playback-set {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 16px 18px;
}
.playback-set:first-child {
  border-top: 3px solid var(--gold);
}
.set-title {
  font-size: 16px;
  font-weight: 800;
  color: var(--ink);
  margin: 0;
}
.set-where {
  font-size: 12.5px;
  color: var(--muted);
  margin: 3px 0 12px;
  line-height: 1.5;
}
.set-keys {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.set-keys li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13.5px;
}
.skip-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-width: 900px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 16px;
  overflow: hidden;
}
.skip-row {
  display: flex;
  gap: 18px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--line);
}
.skip-row:last-child {
  border-bottom: 0;
}
.skip-none {
  background: #fafbfd;
}
.skip-keys {
  width: 92px;
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-content: flex-start;
  padding-top: 2px;
}
.skip-body {
  flex: 1;
  min-width: 0;
}
.skip-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--ink);
  margin: 0;
}
.skip-text {
  font-size: 14px;
  line-height: 1.6;
  margin: 2px 0 0;
  max-width: 68ch;
}
.skip-links {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.skip-links:empty {
  display: none;
}

/* ================= Focus + motion ================= */
button:focus-visible {
  outline: 2px solid var(--gold);
  outline-offset: 2px;
}
@media (prefers-reduced-motion: reduce) {
  .detail-panel,
  .flash {
    animation: none;
  }
  * {
    transition: none !important;
  }
}

/* ================= Responsive ================= */
@media (max-width: 1263px) {
  .playback-sets {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 959px) {
  .help-page {
    padding: 14px 14px 40px;
  }
  .phone-layout,
  .web-layout {
    grid-template-columns: minmax(0, 1fr);
  }
  .tree-panel {
    position: static;
    max-height: 300px;
  }
  .web-rail {
    position: static;
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 4px;
  }
  .rail-link {
    white-space: nowrap;
    border-inline-start: 0;
    border-bottom: 3px solid transparent;
  }
  .rail-link.active {
    border-bottom-color: var(--gold);
  }
  .web-item {
    grid-template-columns: minmax(0, 1fr);
    gap: 4px;
  }
  .detail-panel {
    padding: 18px 16px 22px;
  }
  .detail-title {
    font-size: 22px;
  }
}
@media (max-width: 599px) {
  .modes-block {
    grid-template-columns: minmax(0, 1fr);
  }
  .playback-sets {
    grid-template-columns: minmax(0, 1fr);
  }
  .keypad {
    gap: 8px;
  }
  .pad-key {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 6px;
    min-height: 92px;
    padding: 10px 6px;
  }
  .pad-label {
    font-size: 12.5px;
  }
  .keypad-extra {
    grid-template-columns: minmax(0, 1fr);
  }
  .result-item {
    flex-wrap: wrap;
    gap: 8px;
  }
  .result-crumb {
    min-width: 0;
  }
  .skip-row {
    flex-direction: column;
    gap: 8px;
  }
  .skip-keys {
    width: auto;
  }
  .hero-title {
    font-size: 19px;
  }
}
</style>